import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {get, ref} from 'firebase/database';
import {database} from '../../services/firebase';
import {useAuth} from '../../contexts/AuthContext';
import Header from '../../components/common/Header';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

export default function PlayerDetail() {
    const {playerId} = useParams();
    const navigate = useNavigate();
    const {userRole} = useAuth();
    const [player, setPlayer] = useState(null);
    const [quizResults, setQuizResults] = useState([]);
    const [categoryPerformance, setCategoryPerformance] = useState([]);
    const [bookPerformance, setBookPerformance] = useState([]);
    const [recentGames, setRecentGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('overview');

    // Colors for charts
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];

    useEffect(() => {
        async function fetchPlayerData() {
            try {
                if (userRole !== 'admin') {
                    setError('Accès non autorisé. Vous devez être administrateur pour voir cette page.');
                    setLoading(false);
                    return;
                }

                // Récupérer les données du joueur
                const playerRef = ref(database, `users/${playerId}`);
                const playerSnapshot = await get(playerRef);

                if (!playerSnapshot.exists()) {
                    setError('Joueur non trouvé.');
                    setLoading(false);
                    return;
                }

                const playerData = playerSnapshot.val();
                setPlayer(playerData);

                // Récupérer les résultats des quiz depuis gameResults
                const gameResultsRef = ref(database, 'gameResults');
                const gameResultsSnapshot = await get(gameResultsRef);

                if (gameResultsSnapshot.exists()) {
                    const gameResultsData = gameResultsSnapshot.val();
                    const playerResults = [];

                    // Parcourir tous les quiz
                    Object.entries(gameResultsData).forEach(([quizId, sessionsData]) => {
                        // Parcourir toutes les sessions de chaque quiz
                        Object.entries(sessionsData).forEach(([sessionId, sessionData]) => {
                            // Vérifier si le joueur a participé à cette session
                            if (sessionData.players && sessionData.players[playerId]) {
                                const playerSessionData = sessionData.players[playerId];

                                playerResults.push({
                                    quizId,
                                    sessionId,
                                    quizTitle: sessionData.quizData?.title || `Quiz ${quizId}`,
                                    score: playerSessionData.score || 0,
                                    questionsCorrect: playerSessionData.questionsCorrect || 0,
                                    totalQuestions: playerSessionData.totalQuestions || 0,
                                    completedAt: sessionData.endedAt || null,
                                    averageResponseTime: playerSessionData.averageResponseTime || 0,
                                    successRate: playerSessionData.totalQuestions > 0
                                        ? (playerSessionData.questionsCorrect / playerSessionData.totalQuestions) * 100
                                        : 0
                                });
                            }
                        });
                    });

                    // Trier par date de complétion (plus récent en premier)
                    playerResults.sort((a, b) => {
                        if (!a.completedAt) return 1;
                        if (!b.completedAt) return -1;
                        return new Date(b.completedAt) - new Date(a.completedAt);
                    });

                    setQuizResults(playerResults);
                    setRecentGames(playerResults.slice(0, 5));
                }

                // Traiter les performances par catégorie
                if (playerData.trainingStats && playerData.trainingStats.categoryPerformance) {
                    const categoryData = Object.entries(playerData.trainingStats.categoryPerformance).map(([category, stats]) => ({
                        name: category,
                        asked: stats.asked || 0,
                        correct: stats.correct || 0,
                        percentSuccess: stats.asked > 0 ? Math.round((stats.correct / stats.asked) * 100) : 0
                    }));

                    setCategoryPerformance(categoryData);
                }

                // Traiter les performances par livre biblique
                if (playerData.trainingStats && playerData.trainingStats.bookPerformance) {
                    const bookData = Object.entries(playerData.trainingStats.bookPerformance).map(([book, stats]) => ({
                        name: book,
                        asked: stats.asked || 0,
                        correct: stats.correct || 0,
                        percentSuccess: stats.asked > 0 ? Math.round((stats.correct / stats.asked) * 100) : 0
                    }));

                    // Ne conserver que les 10 livres les plus questionnés
                    bookData.sort((a, b) => b.asked - a.asked);
                    setBookPerformance(bookData.slice(0, 10));
                }

            } catch (error) {
                console.error("Erreur lors de la récupération des données du joueur:", error);
                setError("Une erreur s'est produite lors du chargement des données du joueur.");
            } finally {
                setLoading(false);
            }
        }

        fetchPlayerData();
    }, [playerId, userRole]);

    // Formatage de la date
    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';

        let date;
        if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else {
            date = new Date(timestamp);
        }

        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Calcul du temps moyen de réponse en secondes, formaté
    const formatAverageTime = (timeInSeconds) => {
        if (!timeInSeconds || timeInSeconds === 0) return 'N/A';

        if (timeInSeconds < 60) {
            return `${timeInSeconds.toFixed(1)} sec`;
        } else {
            const minutes = Math.floor(timeInSeconds / 60);
            const seconds = Math.round(timeInSeconds % 60);
            return `${minutes}min ${seconds}sec`;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                    <div className="text-center">
                        <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">Erreur</h3>
                        <p className="mt-1 text-sm text-gray-500">{error}</p>
                        <div className="mt-4">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/players')}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Retour à la liste des joueurs
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!player) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                    <div className="text-center">
                        <svg className="mx-auto h-12 w-12 text-yellow-500" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">Joueur non trouvé</h3>
                        <p className="mt-1 text-sm text-gray-500">Le joueur demandé n'existe pas ou a été supprimé.</p>
                        <div className="mt-4">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/players')}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            >
                                Retour à la liste des joueurs
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Calcul du taux de réussite global
    const successRate = player.trainingStats ?
        (player.trainingStats.correctAnswers / player.trainingStats.questionsAnswered) * 100 || 0 : 0;

    return (
        <div className="min-h-screen bg-gray-100">
            <Header title={`Profil du joueur: ${player.profile?.displayName || 'Anonyme'}`}/>

            <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                <div className="mb-6 flex justify-between items-center">
                    <button
                        type="button"
                        onClick={() => navigate('/admin/players')}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                        </svg>
                        Retour à la liste des joueurs
                    </button>
                </div>

                {/* Profil du joueur */}
                <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
                    <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                        <div>
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                {player.profile?.displayName || 'Joueur anonyme'}
                            </h3>
                            <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                {player.profile?.email || 'Email non disponible'}
                            </p>
                        </div>
                        <div className="flex flex-col items-end">
                            <span
                                className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                                {player.profile?.role || 'joueur'}
                            </span>
                            <span className="text-sm text-gray-500 mt-1">
                                Inscrit le: {formatDate(player.profile?.createdAt)}
                            </span>
                        </div>
                    </div>
                    <div className="border-t border-gray-200">
                        <dl>
                            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Dernière connexion</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {formatDate(player.profile?.lastLogin)}
                                </dd>
                            </div>
                            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Score total</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {player.gameHistory?.totalScore || 0} points
                                </dd>
                            </div>
                            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Parties jouées</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {player.gameHistory?.totalGames || 0}
                                </dd>
                            </div>
                            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Dernière partie</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {formatDate(player.gameHistory?.lastPlayed)}
                                </dd>
                            </div>
                            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Questions répondues</dt>
                                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                    {player.trainingStats?.questionsAnswered || 0}
                                </dd>
                            </div>
                            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                <dt className="text-sm font-medium text-gray-500">Taux de réussite</dt>
                                <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
                                    <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                            successRate > 75
                                                ? 'bg-green-100 text-green-800'
                                                : successRate > 50
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-red-100 text-red-800'
                                        }`}>
                                        {Math.round(successRate)}%
                                    </span>
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>

                {/* Onglets */}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex" aria-label="Tabs">
                            <button
                                onClick={() => setActiveTab('overview')}
                                className={`w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                                    activeTab === 'overview'
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Vue d'ensemble
                            </button>
                            <button
                                onClick={() => setActiveTab('quizzes')}
                                className={`w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                                    activeTab === 'quizzes'
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Quiz complétés
                            </button>
                            <button
                                onClick={() => setActiveTab('categories')}
                                className={`w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                                    activeTab === 'categories'
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Performance par catégorie
                            </button>
                            <button
                                onClick={() => setActiveTab('books')}
                                className={`w-1/4 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                                    activeTab === 'books'
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                            >
                                Livres bibliques
                            </button>
                        </nav>
                    </div>

                    <div className="p-6">
                        {/* Vue d'ensemble */}
                        {activeTab === 'overview' && (
                            <div>
                                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Vue d'ensemble des
                                    performances</h3>

                                {/* Graphique d'évolution des scores */}
                                <div className="mb-6">
                                    <div className="bg-white overflow-hidden shadow-sm rounded-lg">
                                        <div className="px-4 py-5 sm:px-6">
                                            <h3 className="text-lg font-medium leading-6 text-gray-900">Évolution des
                                                scores</h3>
                                            <p className="mt-1 max-w-2xl text-sm text-gray-500">Progression des 10
                                                derniers quiz</p>
                                        </div>
                                        <div className="px-4 py-5 sm:p-6">
                                            <div className="h-80">
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart
                                                        data={quizResults.slice(0, 10).reverse()}
                                                        margin={{top: 5, right: 30, left: 20, bottom: 5}}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3"/>
                                                        <XAxis dataKey="quizTitle" height={60} angle={-45}
                                                               textAnchor="end" interval={0}/>
                                                        <YAxis/>
                                                        <Tooltip/>
                                                        <Legend/>
                                                        <Line type="monotone" dataKey="score" name="Score"
                                                              stroke="#8884d8" activeDot={{r: 8}}/>
                                                        <Line type="monotone" dataKey="successRate"
                                                              name="Taux de réussite (%)" stroke="#82ca9d"/>
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Parties récentes */}
                                <div className="mt-6">
                                    <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Activités
                                        récentes</h3>
                                    <div
                                        className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                        <table className="min-w-full divide-y divide-gray-300">
                                            <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col"
                                                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Quiz
                                                </th>
                                                <th scope="col"
                                                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date
                                                </th>
                                                <th scope="col"
                                                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Score
                                                </th>
                                                <th scope="col"
                                                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Réussite
                                                </th>
                                                <th scope="col"
                                                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Temps
                                                    moyen
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 bg-white">
                                            {recentGames.length === 0 ? (
                                                <tr>
                                                    <td colSpan={5}
                                                        className="py-4 pl-4 pr-3 text-sm text-center text-gray-500 sm:pl-6">
                                                        Aucune partie récente
                                                    </td>
                                                </tr>
                                            ) : (
                                                recentGames.map((game, index) => (
                                                    <tr key={index}>
                                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                            {game.quizTitle}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {formatDate(game.completedAt)}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {game.score} pts
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                <span
                                                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                        game.successRate > 75
                                                                            ? 'bg-green-100 text-green-800'
                                                                            : game.successRate > 50
                                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                                : 'bg-red-100 text-red-800'
                                                                    }`}>
                                                                    {Math.round(game.successRate)}%
                                                                </span>
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {formatAverageTime(game.averageResponseTime)}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Quiz complétés */}
                        {activeTab === 'quizzes' && (
                            <div>
                                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Liste des quiz
                                    complétés</h3>

                                {quizResults.length === 0 ? (
                                    <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6 text-center">
                                        <p className="text-sm text-gray-500">Ce joueur n'a complété aucun quiz.</p>
                                    </div>
                                ) : (
                                    <div>
                                        {/* Graphique de performance par quiz */}
                                        <div className="mb-6">
                                            <div className="bg-white overflow-hidden shadow-sm rounded-lg">
                                                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                                                    <h3 className="text-lg font-medium leading-6 text-gray-900">Taux de
                                                        réussite par quiz</h3>
                                                </div>
                                                <div className="px-4 py-5 sm:p-6">
                                                    <div className="h-80">
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <BarChart
                                                                data={quizResults.slice(0, 10)}
                                                                margin={{top: 20, right: 30, left: 20, bottom: 70}}
                                                            >
                                                                <CartesianGrid strokeDasharray="3 3"/>
                                                                <XAxis dataKey="quizTitle" angle={-45} textAnchor="end"
                                                                       height={70}/>
                                                                <YAxis/>
                                                                <Tooltip/>
                                                                <Legend/>
                                                                <Bar dataKey="score" name="Score" fill="#8884d8"/>
                                                                <Bar dataKey="successRate" name="Taux de réussite (%)"
                                                                     fill="#82ca9d"/>
                                                            </BarChart>
                                                        </ResponsiveContainer>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Liste des quiz */}
                                        <div
                                            className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                            <table className="min-w-full divide-y divide-gray-300">
                                                <thead className="bg-gray-50">
                                                <tr>
                                                    <th scope="col"
                                                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Quiz
                                                    </th>
                                                    <th scope="col"
                                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Date
                                                    </th>
                                                    <th scope="col"
                                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Score
                                                    </th>
                                                    <th scope="col"
                                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Réponses
                                                    </th>
                                                    <th scope="col"
                                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Taux
                                                    </th>
                                                    <th scope="col"
                                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Temps
                                                        moy.
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 bg-white">
                                                {quizResults.map((result, index) => (
                                                    <tr key={index}>
                                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                            {result.quizTitle}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {formatDate(result.completedAt)}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {result.score} pts
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {result.questionsCorrect}/{result.totalQuestions}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                <span
                                                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                        result.successRate > 75
                                                                            ? 'bg-green-100 text-green-800'
                                                                            : result.successRate > 50
                                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                                : 'bg-red-100 text-red-800'
                                                                    }`}>
                                                                    {Math.round(result.successRate)}%
                                                                </span>
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {formatAverageTime(result.averageResponseTime)}
                                                        </td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Performance par catégorie */}
                        {activeTab === 'categories' && (
                            <div>
                                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Analyse par
                                    catégorie</h3>

                                {categoryPerformance.length === 0 ? (
                                    <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6 text-center">
                                        <p className="text-sm text-gray-500">Pas assez de données pour analyser les
                                            performances par catégorie.</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {/* Graphique camembert */}
                                        <div className="bg-white overflow-hidden shadow-sm rounded-lg">
                                            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                                                <h3 className="text-lg font-medium leading-6 text-gray-900">Taux de
                                                    réussite par catégorie</h3>
                                            </div>
                                            <div className="px-4 py-5 sm:p-6">
                                                <div className="h-80">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <PieChart>
                                                            <Pie
                                                                data={categoryPerformance}
                                                                cx="50%"
                                                                cy="50%"
                                                                labelLine={true}
                                                                outerRadius={100}
                                                                fill="#8884d8"
                                                                dataKey="percentSuccess"
                                                                nameKey="name"
                                                                label={({
                                                                            name,
                                                                            percentSuccess
                                                                        }) => `${name}: ${percentSuccess}%`}
                                                            >
                                                                {categoryPerformance.map((entry, index) => (
                                                                    <Cell key={`cell-${index}`}
                                                                          fill={COLORS[index % COLORS.length]}/>
                                                                ))}
                                                            </Pie>
                                                            <Tooltip formatter={(value) => `${value}%`}/>
                                                            <Legend/>
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Graphique radar */}
                                        <div className="bg-white overflow-hidden shadow-sm rounded-lg">
                                            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                                                <h3 className="text-lg font-medium leading-6 text-gray-900">Force par
                                                    catégorie</h3>
                                            </div>
                                            <div className="px-4 py-5 sm:p-6">
                                                <div className="h-80">
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <RadarChart cx="50%" cy="50%" outerRadius="80%"
                                                                    data={categoryPerformance}>
                                                            <PolarGrid/>
                                                            <PolarAngleAxis dataKey="name"/>
                                                            <PolarRadiusAxis angle={30} domain={[0, 100]}/>
                                                            <Radar name="Taux de réussite" dataKey="percentSuccess"
                                                                   stroke="#8884d8" fill="#8884d8" fillOpacity={0.6}/>
                                                            <Tooltip formatter={(value) => `${value}%`}/>
                                                            <Legend/>
                                                        </RadarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Liste des catégories */}
                                        <div
                                            className="lg:col-span-2 overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                            <table className="min-w-full divide-y divide-gray-300">
                                                <thead className="bg-gray-50">
                                                <tr>
                                                    <th scope="col"
                                                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Catégorie
                                                    </th>
                                                    <th scope="col"
                                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Questions
                                                        posées
                                                    </th>
                                                    <th scope="col"
                                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Réponses
                                                        correctes
                                                    </th>
                                                    <th scope="col"
                                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Taux
                                                        de réussite
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 bg-white">
                                                {categoryPerformance.map((category, index) => (
                                                    <tr key={index}>
                                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                            {category.name}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {category.asked}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {category.correct}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                <span
                                                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                        category.percentSuccess > 75
                                                                            ? 'bg-green-100 text-green-800'
                                                                            : category.percentSuccess > 50
                                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                                : 'bg-red-100 text-red-800'
                                                                    }`}>
                                                                    {category.percentSuccess}%
                                                                </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Livres bibliques */}
                        {activeTab === 'books' && (
                            <div>
                                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Performance par livre
                                    biblique</h3>

                                {bookPerformance.length === 0 ? (
                                    <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6 text-center">
                                        <p className="text-sm text-gray-500">Pas assez de données pour analyser les
                                            performances par livre biblique.</p>
                                    </div>
                                ) : (
                                    <div>
                                        {/* Graphique à barres horizontales */}
                                        <div className="mb-6">
                                            <div className="bg-white overflow-hidden shadow-sm rounded-lg">
                                                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                                                    <h3 className="text-lg font-medium leading-6 text-gray-900">Top 10
                                                        des livres les plus étudiés</h3>
                                                </div>
                                                <div className="px-4 py-5 sm:p-6">
                                                    <div className="h-96">
                                                        <ResponsiveContainer width="100%" height="100%">
                                                            <BarChart
                                                                layout="vertical"
                                                                data={bookPerformance}
                                                                margin={{top: 20, right: 30, left: 100, bottom: 5}}
                                                            >
                                                                <CartesianGrid strokeDasharray="3 3"/>
                                                                <XAxis type="number"/>
                                                                <YAxis dataKey="name" type="category" width={90}/>
                                                                <Tooltip/>
                                                                <Legend/>
                                                                <Bar dataKey="asked" name="Questions posées"
                                                                     fill="#8884d8"/>
                                                                <Bar dataKey="correct" name="Réponses correctes"
                                                                     fill="#82ca9d"/>
                                                            </BarChart>
                                                        </ResponsiveContainer>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Liste des livres */}
                                        <div
                                            className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                                            <table className="min-w-full divide-y divide-gray-300">
                                                <thead className="bg-gray-50">
                                                <tr>
                                                    <th scope="col"
                                                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Livre
                                                    </th>
                                                    <th scope="col"
                                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Questions
                                                        posées
                                                    </th>
                                                    <th scope="col"
                                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Réponses
                                                        correctes
                                                    </th>
                                                    <th scope="col"
                                                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Taux
                                                        de réussite
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200 bg-white">
                                                {bookPerformance.map((book, index) => (
                                                    <tr key={index}>
                                                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                                            {book.name}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {book.asked}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                            {book.correct}
                                                        </td>
                                                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                                <span
                                                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                                        book.percentSuccess > 75
                                                                            ? 'bg-green-100 text-green-800'
                                                                            : book.percentSuccess > 50
                                                                                ? 'bg-yellow-100 text-yellow-800'
                                                                                : 'bg-red-100 text-red-800'
                                                                    }`}>
                                                                    {book.percentSuccess}%
                                                                </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
